import type { components } from "@oathera/contract/api";
import type { StatusLevel } from "../../components/index.ts";
import type { AuditChain, AuditKeyPin, CoreIntegrityRow } from "./use-integrity.ts";

/**
 * Presentation vocabulary for the Integrity screen (task 11.9; design 12.2 Integrity row, 12.3/12.4
 * principles). It keeps the one-way maps from the generated contract shapes onto the screen's
 * presentational pieces in a single, testable place and defines NO API shapes (those come from
 * `@oathera/contract/api` via `use-integrity.ts`).
 *
 * THE CRITICAL `pending`/`none` SEMANTICS (design 12.2; the named, testable item in 11.17's list).
 * For EVERY audit chain the screen ALWAYS shows four things — verification level, head position,
 * export lag, and first failure. Two contract sentinels drive the hardest part, and they mean
 * DIFFERENT things and must stay visually and semantically distinct:
 *
 *   1. `verification_level === "pending"` — the level is UNDETERMINED (not yet verified / in
 *      progress). The contract enum is `pending | 0 | 1 | 2` (0 received-only, 1 batch-level, 2
 *      entry-level). `pending` is NOT a pass: "unverifiable is never green" (design 12.3), so
 *      {@link verificationLevelStatus} maps `pending` to the grey `unknown` level, never `healthy`.
 *      A level that is merely low (0, received-only) is also NOT green — it is a warn — because a
 *      received-but-unverified chain is likewise not confirmed.
 *
 *   2. `first_failure === "none"` — there is NO first failure: the chain is clean so far. This is a
 *      DETERMINED, positive fact (verified-and-clean), the opposite of `pending`. It is distinct
 *      from "unknown"/"pending": `none` means "checked, nothing wrong", whereas a `pending` level
 *      means "not yet determined". {@link firstFailureView} returns a dedicated `none` kind so the
 *      screen can render it as a calm "No failures" rather than as a status that could be confused
 *      with pending.
 *
 * So: an undetermined LEVEL (`pending`, grey) and a determined-clean FIRST FAILURE (`none`) are two
 * different concepts and the maps below keep them apart.
 *
 * The screen's overall per-row status comes from the chain `status` enum
 * (`ok | failed | unverifiable | stale`); `unverifiable` is distinct from `failed` and, per design,
 * is never green. The maps here are the single place those contract enums become `StatusLevel`s,
 * human labels and `/events` drill links.
 */

type AuditVerificationLevel = components["schemas"]["AuditVerificationLevel"];
type AuditChainStatus = components["schemas"]["AuditChainStatus"];
type AuditFailureReason = components["schemas"]["AuditFailureReason"];
type AuditKeyState = components["schemas"]["AuditKeyState"];
type AuditKeyPolicy = components["schemas"]["AuditKeyPolicy"];
type CoreState = components["schemas"]["enum_core_state"];
type CoreFailureReason = components["schemas"]["enum_core_failure_reason"];

/* ---- Protected_Core state ------------------------------------------------------------------- */

/**
 * Map a Protected_Core `state` onto a status level (design 12.4: icon+text, 12.3: never green when
 * not confirmed). Only an explicitly `verified` core is healthy; `degraded` is a warn; `failed` and
 * `absent` are errors; anything else (`other`) is grey `unknown` — never green. A `stale` reading
 * (below) further downgrades a would-be-healthy state so a silent instance can never read "all good".
 */
export function coreStateStatus(state: CoreState): StatusLevel {
  switch (state) {
    case "verified":
      return "healthy";
    case "degraded":
      return "warn";
    case "failed":
    case "absent":
      return "error";
    case "other":
      return "unknown";
  }
}

/** Human label for a Protected_Core state. */
export function coreStateLabel(state: CoreState): string {
  switch (state) {
    case "verified":
      return "Verified";
    case "degraded":
      return "Degraded";
    case "failed":
      return "Failed";
    case "absent":
      return "Absent";
    case "other":
      return "Other";
  }
}

/**
 * The status level to SHOW for a Protected_Core row, folding in `stale`. A stale reading is not
 * trustworthy, so even a `verified` state is downgraded to grey `unknown` when stale — a silent
 * instance is never shown as green (design 12.3). A non-healthy state keeps its own (worse) level.
 */
export function coreRowStatus(row: CoreIntegrityRow): StatusLevel {
  const base = coreStateStatus(row.state);
  if (row.stale && base === "healthy") return "unknown";
  return base;
}

/** Human label for a Protected_Core failure reason. */
export function coreFailureReasonLabel(reason: CoreFailureReason): string {
  switch (reason) {
    case "core_missing":
      return "Core missing";
    case "hash_mismatch":
      return "Hash mismatch";
    case "signature_invalid":
      return "Signature invalid";
    case "load_failed":
      return "Load failed";
    case "target_mismatch":
      return "Target mismatch";
    case "other":
      return "Other";
  }
}

/* ---- Audit chain: verification level (the `pending` concept) --------------------------------- */

/**
 * Map a chain's verification level onto a status level. CRITICAL (design 12.3 "unverifiable is never
 * green"): `pending` — an UNDETERMINED level — is grey `unknown`, NEVER green. A low determined
 * level (0, received-only) is a warn (received but not cryptographically verified). Only the full
 * entry-level verification (2) is healthy; batch-level (1) is a warn because entries are not yet
 * individually verified. This keeps a not-yet-verified chain from ever reading as confirmed/green.
 */
export function verificationLevelStatus(level: AuditVerificationLevel): StatusLevel {
  if (level === "pending") return "unknown";
  switch (level) {
    case 2:
      return "healthy";
    case 1:
      return "warn";
    case 0:
      return "warn";
  }
}

/**
 * Human label for a verification level. `pending` is spelled out as "Pending" so it reads as an
 * undetermined state, never as a pass. The numeric levels name what WAS verified.
 */
export function verificationLevelLabel(level: AuditVerificationLevel): string {
  if (level === "pending") return "Pending";
  switch (level) {
    case 0:
      return "Received only";
    case 1:
      return "Batch-level";
    case 2:
      return "Entry-level";
  }
}

/** A longer accessible phrase for a verification level, used as the badge's `title`. */
export function verificationLevelTitle(level: AuditVerificationLevel): string {
  if (level === "pending") {
    return "Verification level: pending, not yet determined, so this chain is not shown as verified.";
  }
  switch (level) {
    case 0:
      return "Verification level 0: batches received only, not yet cryptographically verified.";
    case 1:
      return "Verification level 1: batch roots and links verified; entries not individually verified.";
    case 2:
      return "Verification level 2: entries individually verified.";
  }
}

/** True when the level is the undetermined `pending` sentinel (grey-not-green). */
export function isLevelPending(level: AuditVerificationLevel): boolean {
  return level === "pending";
}

/* ---- Audit chain: status --------------------------------------------------------------------- */

/**
 * Map a chain `status` onto a status level (design 12.3: `unverifiable` is distinct from `failed`
 * and is never green). `ok` is healthy; `failed` is an error; `unverifiable` and `stale` are grey
 * `unknown` — not green, not an error, honestly "we could not confirm this".
 */
export function chainStatusLevel(status: AuditChainStatus): StatusLevel {
  switch (status) {
    case "ok":
      return "healthy";
    case "failed":
      return "error";
    case "unverifiable":
    case "stale":
      return "unknown";
  }
}

/** Human label for a chain status. */
export function chainStatusLabel(status: AuditChainStatus): string {
  switch (status) {
    case "ok":
      return "OK";
    case "failed":
      return "Failed";
    case "unverifiable":
      return "Unverifiable";
    case "stale":
      return "Stale";
  }
}

/** Human label for a chain's `status_reason` (the why behind a non-ok status), null-safe. */
export function chainStatusReasonLabel(
  reason: AuditChain["status_reason"],
): string | null {
  switch (reason) {
    case "jwks_unreachable":
      return "Key source unreachable";
    case "key_unpinned":
      return "Key not pinned";
    case "awaiting_batches":
      return "Awaiting batches";
    case null:
      return null;
  }
}

/* ---- Audit chain: first failure (the `none` concept) ----------------------------------------- */

/**
 * A rendered view of a chain's first failure. The `none` kind is a DETERMINED, positive fact (the
 * chain is clean so far) and is deliberately a different shape from a failure — so the screen can
 * render it as a calm "No failures" that can never be confused with the undetermined `pending`
 * verification level. The `failure` kind carries the reason, the batch seq (nullable per contract)
 * and when it was detected.
 */
export type FirstFailureView =
  | { readonly kind: "none" }
  | {
      readonly kind: "failure";
      readonly reason: AuditFailureReason;
      readonly reasonLabel: string;
      readonly batchSeq: number | null;
      readonly detectedAtMs: number;
    };

/**
 * Derive the first-failure view from the contract's `first_failure`, which is the string literal
 * `none` OR an {@link AuditFirstFailure} object. `none` → the clean `none` view; an object → a
 * `failure` view with a human reason label. This is the ONLY place the `none` sentinel is turned
 * into presentation, keeping it distinct from the `pending` level everywhere else.
 */
export function firstFailureView(firstFailure: AuditChain["first_failure"]): FirstFailureView {
  if (firstFailure === "none") return { kind: "none" };
  return {
    kind: "failure",
    reason: firstFailure.reason,
    reasonLabel: auditFailureReasonLabel(firstFailure.reason),
    batchSeq: firstFailure.batch_seq,
    detectedAtMs: firstFailure.detected_at,
  };
}

/** True when the chain reports NO first failure (the clean `none` sentinel). */
export function hasNoFirstFailure(firstFailure: AuditChain["first_failure"]): boolean {
  return firstFailure === "none";
}

/** Human label for an audit failure reason. */
export function auditFailureReasonLabel(reason: AuditFailureReason): string {
  switch (reason) {
    case "audit_batch_root_mismatch":
      return "Batch root mismatch";
    case "audit_batch_link_broken":
      return "Batch link broken";
    case "audit_batch_gap":
      return "Batch gap";
    case "audit_seq_reset":
      return "Sequence reset";
    case "audit_batch_signature_invalid":
      return "Batch signature invalid";
    case "audit_kid_unknown":
      return "Unknown key id";
    case "audit_entry_hash_mismatch":
      return "Entry hash mismatch";
    case "audit_chain_link_broken":
      return "Chain link broken";
    case "audit_unsealed_tail":
      return "Unsealed tail";
    case "audit_equivocation":
      return "Equivocation";
  }
}

/* ---- Audit chain: head position & export lag ------------------------------------------------- */

/**
 * A rendered head position. All members are `null` before the first batch (per contract), which the
 * screen shows honestly as "No batches yet" rather than fabricating a position.
 */
export function headPositionText(head: AuditChain["head"]): string {
  if (head.batch_seq === null && head.last_entry_seq === null) return "No batches yet";
  const batch = head.batch_seq === null ? "n/a" : `#${head.batch_seq}`;
  const entry = head.last_entry_seq === null ? "n/a" : `#${head.last_entry_seq}`;
  return `batch ${batch} · entry ${entry}`;
}

/** The head's batch hash, shortened for display, or null when there is no batch yet. */
export function headHashShort(head: AuditChain["head"]): string | null {
  if (head.batch_hash === null) return null;
  const hash = head.batch_hash;
  return hash.length > 16 ? `${hash.slice(0, 8)}…${hash.slice(-4)}` : hash;
}

/** A rendered export-lag view: the human text and whether a growing lag is a warning signal. */
export interface ExportLagView {
  /** The lag in words (always shown; honest "unknown" when the server could not measure it). */
  readonly text: string;
  /** True when the lag is large enough to flag as a warning (a growing lag is a warning signal). */
  readonly isWarning: boolean;
  /** The last-export time in ms, or null when nothing has been exported yet. */
  readonly lastExportedAtMs: number | null;
}

/** The lag threshold (ms) past which a growing export lag is flagged as a warning signal. */
export const EXPORT_LAG_WARN_MS = 60_000;
/** The batch-count lag past which the export lag is flagged as a warning signal. */
export const EXPORT_LAG_WARN_BATCHES = 4;

/**
 * Derive the export-lag view. The lag is ALWAYS shown (design 12.2) — when the server could not
 * measure it (`lag_ms`/`lag_batches` null), that is shown honestly as "Unknown" rather than hidden
 * or treated as zero. A lag over {@link EXPORT_LAG_WARN_MS} or {@link EXPORT_LAG_WARN_BATCHES} is a
 * warning signal (a growing lag means audit batches are not reaching durable export promptly).
 */
export function exportLagView(exportLag: AuditChain["export_lag"]): ExportLagView {
  const { lag_ms, lag_batches, last_exported_at } = exportLag;
  if (last_exported_at === null && lag_ms === null && lag_batches === null) {
    return { text: "No export yet", isWarning: true, lastExportedAtMs: null };
  }
  if (lag_ms === null && lag_batches === null) {
    return { text: "Unknown", isWarning: false, lastExportedAtMs: last_exported_at };
  }
  const parts: string[] = [];
  if (lag_ms !== null) parts.push(formatDurationMs(lag_ms));
  if (lag_batches !== null) parts.push(`${lag_batches} batch${lag_batches === 1 ? "" : "es"}`);
  const isWarning =
    (lag_ms !== null && lag_ms > EXPORT_LAG_WARN_MS) ||
    (lag_batches !== null && lag_batches > EXPORT_LAG_WARN_BATCHES);
  return { text: parts.join(" · "), isWarning, lastExportedAtMs: last_exported_at };
}

/** A compact human duration for a millisecond lag. */
export function formatDurationMs(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  return `${h}h`;
}

/* ---- Key pins: pinned vs observed ------------------------------------------------------------ */

/**
 * Map an audit key state onto a status level and whether it is PINNED. The design wants pinned vs
 * observed distinguished by icon+text, never colour alone (12.4): a `pinned` key is healthy
 * (trusted); an `observed_unpinned` key (trust-on-first-use / observed, not yet approved) is a warn
 * — it is NOT green because it is not yet a pinned trust anchor; a `retired` key is grey `unknown`.
 */
export function keyStateStatus(state: AuditKeyState): StatusLevel {
  switch (state) {
    case "pinned":
      return "healthy";
    case "observed_unpinned":
      return "warn";
    case "retired":
      return "unknown";
  }
}

/** Human label for an audit key state, distinguishing pinned from observed-unpinned clearly. */
export function keyStateLabel(state: AuditKeyState): string {
  switch (state) {
    case "pinned":
      return "Pinned";
    case "observed_unpinned":
      return "Observed (unpinned)";
    case "retired":
      return "Retired";
  }
}

/** True when a key is a pinned trust anchor (vs merely observed). */
export function isKeyPinned(pin: AuditKeyPin): boolean {
  return pin.state === "pinned";
}

/** The glyph label used (alongside the status badge) to distinguish pinned vs observed by text. */
export function keyPinKindLabel(pin: AuditKeyPin): string {
  return pin.state === "pinned" ? "Pinned key" : "Observed key";
}

/**
 * Human label for a chain's key policy. `tofu` (trust on first use) is always shown explicitly
 * (FR-11.5) so an operator can see a chain is on first-use trust rather than pinned.
 */
export function keyPolicyLabel(policy: AuditKeyPolicy): string {
  return policy === "pinned" ? "Pinned" : "Trust on first use";
}

/** A short display form of a JWK thumbprint (base64url, 43 chars) for a key row. */
export function thumbprintShort(thumbprint: string): string {
  return thumbprint.length > 14 ? `${thumbprint.slice(0, 8)}…${thumbprint.slice(-4)}` : thumbprint;
}

/* ---- Emptiness ------------------------------------------------------------------------------- */

/** True when there is no Protected_Core instance AND no audit chain to show. */
export function isIntegrityEmpty(
  core: readonly CoreIntegrityRow[],
  chains: readonly AuditChain[],
): boolean {
  return core.length === 0 && chains.length === 0;
}

/* ---- Drill-down to /events ------------------------------------------------------------------- */

/**
 * Build a relative `/events` link from a set of filter params. Values are encoded by
 * `URLSearchParams`; empty values are dropped. The integrity endpoints attach no server-built
 * `drill`, so the screen composes honest standing filters from real `/events` params — never an
 * invented route. The events screen (task 11.12) reads them.
 */
function eventsHref(params: Readonly<Record<string, string>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value.length > 0) search.set(key, value);
  }
  const qs = search.toString();
  return qs.length > 0 ? `/events?${qs}` : "/events";
}

/** The `/events` filter for a chain's audit events (by source + instance). */
export function chainEventsHref(chain: AuditChain): string {
  return eventsHref({ category: "audit", source_id: chain.source_id, instance_id: chain.instance_id });
}

/** The `/events` filter for a Protected_Core instance's integrity events. */
export function coreEventsHref(row: CoreIntegrityRow): string {
  return eventsHref({ category: "core_integrity", source_id: row.source_id, instance_id: row.instance_id });
}
